import Image from "next/image";
import {STRAPI_URL} from "../cms/fetchFromStrapi";
import {FCWithHTMLAttributes} from "../types/reactTypes";
import {IS_STORYBOOK} from "../util/environment";
import wrapWithErrorBoundary from "../util/wrapWithErrorBoundary";

export type StrapiImageData = {
	attributes: {
		url: string;
		alternativeText: string;
		caption: string;
		ext: string;
		hash: string;
		width?: number;
		height?: number;
		size: number;
		fill?: boolean;
	};
};

export type StrapiImageDataData = {
	data: StrapiImageData;
};

// The format when the API returns multiple images is slightly different
export type StrapiImageDataMultiple = (StrapiImageData & {id: string})[];

type ImageWithoutSrcOrAlt = Omit<
	React.ComponentProps<typeof Image>,
	"src" | "alt"
>;

export interface StrapiImageProps extends ImageWithoutSrcOrAlt {
	image: StrapiImageData;
}

/**
 * Component which renders an image from the Strapi API.
 *
 * The `image` prop should be the value from the API corresponding to a Media
 * field, which should have a structure matching the StrapiImageData type.
 */
const StrapiImage: FCWithHTMLAttributes<StrapiImageProps> = ({
	image,
	...props
}) => {
	if (!image?.attributes) {
		return null;
	}
	const {url, alternativeText, width, height} = image.attributes;

	// Workaround for an issue in Strapi: https://github.com/strapi/strapi/issues/21027
	// url may be an absolute url (if it's an image in a rich text block) or a relative
	// url (anywhere else).  If it's an absolute URL, the domain may be incorrect.  This
	// line extracts just the relative part of the url regardless of how it's originally
	// formatted.
	const urlPath = new URL(url, STRAPI_URL).pathname;

	const imageProps = {
		src: `${STRAPI_URL}${urlPath}`,
		width: props.width ?? width,
		height: props.height ?? height,
		...props,
	};
	if (props.layout === "fill" || imageProps.fill) {
		delete imageProps.width;
		delete imageProps.height;
		imageProps.fill = true;
	}
	if (IS_STORYBOOK) {
		// Fall back to a regular <img> tag in Storybook
		return <img src={url} alt={alternativeText} {...(props as any)} />;
	}
	return <Image {...imageProps} alt={alternativeText ?? ""} />;
};

export default wrapWithErrorBoundary(StrapiImage);
